import React, { useState, useMemo } from 'react';
import './CardGrid.css';

/**
 * Find the best printing to display based on preferred language and image availability
 * Priority: 
 * 1. Preferred language with available image
 * 2. English with available image
 * 3. Any language with available image
 * 4. Preferred language (even without image)
 * 5. English (even without image)
 * 6. First available printing
 * 
 * image_status values: 'highres_scan', 'lowres', 'placeholder', 'missing'
 * We consider 'highres_scan' and 'lowres' as having available images
 */
function hasAvailableImage(printing) {
  const status = printing.image_status;
  return status === 'highres_scan' || status === 'lowres';
}

function getBestPrinting(card, preferredLanguage = 'en') {
  const printings = card.printings || [];
  
  if (printings.length === 0) {
    return card; // No printings data, return card as-is
  }
  
  // 1. Preferred language with available image
  const preferredWithImage = printings.find(p => p.lang === preferredLanguage && hasAvailableImage(p));
  if (preferredWithImage) {
    return { ...card, ...preferredWithImage };
  }
  
  // 2. English with available image (if different from preferred)
  if (preferredLanguage !== 'en') {
    const englishWithImage = printings.find(p => p.lang === 'en' && hasAvailableImage(p));
    if (englishWithImage) {
      return { ...card, ...englishWithImage };
    }
  }
  
  // 3. Any language with available image
  const anyWithImage = printings.find(p => hasAvailableImage(p));
  if (anyWithImage) {
    return { ...card, ...anyWithImage };
  }
  
  // 4. Preferred language (even without image)
  const preferredPrinting = printings.find(p => p.lang === preferredLanguage);
  if (preferredPrinting) {
    return { ...card, ...preferredPrinting };
  }
  
  // 5. English (even without image)
  if (preferredLanguage !== 'en') {
    const englishPrinting = printings.find(p => p.lang === 'en');
    if (englishPrinting) {
      return { ...card, ...englishPrinting };
    }
  }
  
  // 6. First available printing
  return { ...card, ...printings[0] };
}

/**
 * Mana Symbols Component - renders SVG mana symbols inline
 */
function ManaSymbolsSmall({ manaCost, apiUrl }) {
  if (!manaCost) return null;
  
  // Match all {X} patterns
  const symbolPattern = /\{[^}]+\}/g;
  const symbols = manaCost.match(symbolPattern) || [];
  
  return (
    <span className="mana-symbols-small">
      {symbols.map((symbol, i) => {
        const filename = symbol.replace(/[{}]/g, '').replace(/\//g, '-');
        const svgUrl = `${apiUrl}/images/mtg/base-assets/Resource-symbols/${filename}.svg`;
        
        return (
          <img 
            key={i}
            src={svgUrl} 
            alt={symbol}
            className="mana-symbol-small"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        );
      })}
    </span>
  );
}

function CardGrid({ cards, onCardClick, apiUrl, preferredLanguage = 'en' }) {
  return (
    <div className="card-grid">
      {cards.map((card) => (
        <CardItem 
          key={card.id} 
          card={card} 
          onClick={() => onCardClick(card)}
          apiUrl={apiUrl}
          preferredLanguage={preferredLanguage}
        />
      ))}
    </div>
  );
}

// Layouts that have card_faces with mana_cost on the face
const TWO_FACED_LAYOUTS = ['transform', 'modal_dfc', 'double_faced_token', 'reversible_card', 'art_series'];

function CardItem({ card, onClick, apiUrl, preferredLanguage }) {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Get the best printing to display based on preferred language
  const displayCard = useMemo(() => 
    getBestPrinting(card, preferredLanguage), 
    [card, preferredLanguage]
  );

  // For DFCs, get mana cost from front face if not on card level
  const manaCost = useMemo(() => {
    // If card has mana_cost at top level, use it
    if (card.mana_cost) return card.mana_cost;
    
    // For two-faced cards, get mana_cost from front face
    if (TWO_FACED_LAYOUTS.includes(card.layout) && card.card_faces?.length > 0) {
      // Sort to ensure front is first
      const sortedFaces = [...card.card_faces].sort((a, b) => {
        if (a.side === 'front') return -1;
        if (b.side === 'front') return 1;
        return 0;
      });
      return sortedFaces[0]?.mana_cost;
    }
    
    return null;
  }, [card]);

  // Language-based image path: <apiUrl>/mtg/<lang>/<id>.jpg
  const lang = displayCard.lang || 'en';
  const imageUrl = `${apiUrl}/images/mtg/${lang}/${displayCard.id}.jpg`;

  const getRarityClass = (rarity) => {
    return `rarity-${rarity || 'common'}`;
  };

  const getColorClass = (colors) => {
    if (!colors || colors.length === 0) return 'color-c';
    if (colors.length > 1) return 'color-multi';
    return `color-${colors[0].toLowerCase()}`;
  };

  return (
    <div 
      className={`card-item ${getRarityClass(displayCard.rarity)}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      <div className="card-image-container">
        {!imageLoaded && !imageError && (
          <div className="card-placeholder">
            <div className="placeholder-icon">◆</div>
            <div className="placeholder-text">Loading...</div>
          </div>
        )}
        {!imageError ? (
          <img
            src={imageUrl}
            alt={displayCard.name}
            className={`card-image ${imageLoaded ? 'loaded' : ''}`}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
            loading="lazy"
          />
        ) : (
          <div className="card-placeholder error">
            <div className={`placeholder-colors ${getColorClass(card.colors)}`}>
              {card.colors?.join('') || 'C'}
            </div>
            <div className="placeholder-name">{card.name}</div>
            <div className="placeholder-type">{card.type_line}</div>
          </div>
        )}
        <div className="card-overlay">
          <span className="view-label">View Details</span>
        </div>
      </div>
      <div className="card-info">
        <h3 className="card-name" title={card.name}>{card.name}</h3>
        <div className="card-meta">
          <span className={`card-rarity ${getRarityClass(displayCard.rarity)}`}>
            {displayCard.rarity?.charAt(0).toUpperCase()}
          </span>
          <span className="card-set" title={displayCard.set_name}>{displayCard.set?.toUpperCase()}</span>
          {displayCard.lang && displayCard.lang !== 'en' && (
            <span className="card-lang" title={`Language: ${displayCard.lang}`}>
              {displayCard.lang.toUpperCase()}
            </span>
          )}
          {manaCost && (
            <ManaSymbolsSmall manaCost={manaCost} apiUrl={apiUrl} />
          )}
        </div>
      </div>
    </div>
  );
}

export default CardGrid;
